import React, { useState } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";
import ServicesTotal from "components/ServicesTotal";
import ServiceRow from "components/ServiceRow";
import Icon from "components/icons";
import PopoverComponent from "components/Popover";
import Price from "components/Price";
// import CardBrand from "components/CardBrand";

const EditLink = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.themeColors.primary};
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  padding: 0;

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ServiceTableTitle = styled.h3`
  margin: 2rem 0 0.5rem;

  div {
    font-size: 1.5rem;
  }
`;

const Service = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  display: flex;
  font-size: 0.875rem;
  font-weight: 400;
  justify-content: flex-start;

  ${({ isDuration }) =>
    isDuration &&
    css`
      color: ${({ theme }) => theme.themeColors.darkGreen4};
      justify-content: flex-end;
    `}

  ${({ isPrice }) =>
    isPrice &&
    css`
      font-weight: 600;
      justify-content: flex-end;
    `}
`;

const ServiceTotalWrapper = styled.div`
  border-top: ${({ theme, showBorder }) =>
    showBorder ? `0.0625rem solid ${theme.themeColors.lightGrey}` : "none"};
  margin: 0.5rem 0 0 0;
  padding: 0.5rem 0 0 0;
`;

const CancellationFeeMessage = styled.div`
  color: ${({ theme, cancellationFee }) =>
    cancellationFee
      ? `${theme.themeColors.danger}`
      : `${theme.themeColors.darkGreen4}`};
  font-size: 0.875rem;
  font-weight: 400;
`;

const PopoverContent = styled.div`
  font-weight: 400;
  max-width: 20.625rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  p {
    color: ${({ theme }) => theme.themeColors.darkGreen4};
    font-size: 0.9rem;
    margin: 0;
  }
  h4 {
    margin-bottom: 0.5rem;
  }
  h6 {
    margin: 0;
    display: inline-flex;
    justify-content: space-between;
  }
`;

/* const CreditCardWrapper = styled.div`
  align-items: center;
  display: flex;
`;

const CardLastFourWrapper = styled.span`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  font-size: 0.875rem;
  font-weight: 400;
  margin-left: 0.25rem;

  ${({ theme }) => theme.mq.lg`
    margin-left: 0.5rem;
  `}
`; */

function ServicesTable({
  discount,
  editHandler,
  services,
  serviceFee,
  subtotal,
  tax,
  total,
  // totalWithTip,
  tipInt,
  skipPayments,
  cancellationFee,
  promoDiscount,
  providerTipFeeInt,
  providerTipInt,
  isCustomer,
  // customerUserPaymentMethod,
  checkingOut,
  computedStatus,
}) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [serviceReferenceElement, setServiceReferenceElement] = useState(null);
  const [tipReferenceElement, setTipReferenceElement] = useState(null);
  const isCancelled = computedStatus === "cancelled";
  // const hasTotal = !!total
  const subtotalInt = subtotal
  subtotal = subtotalInt/100
  const serviceFeeInt = serviceFee
  serviceFee = serviceFeeInt/100
  const totalDuration = () => {
    if (isCancelled) {
      return null;
    }
    return services.reduce(
      (duration, current) => duration + current.attributes.duration,
      0
    );
  };

  const popoverText = (feeText, feeInt) => {
    return (
      <PopoverContent>
        <div>
          <h4>Merchant Services Fee</h4>
          <p>
            For secure payment processing, we partner with Stripe. Stripe
            transaction fees apply to each transaction.
          </p>
        </div>
        <h6>
          {feeText} <Price value={(feeInt || 0) / 100} />
        </h6>
      </PopoverContent>
    );
  };

  const servicePopover = () => {
    if (skipPayments) {
      return null;
    }
    return (
      <PopoverComponent
        referenceElement={serviceReferenceElement}
        placement="right"
      >
        {popoverText("Cost of Services Fee (2.9% + $0.30)", serviceFee)}
      </PopoverComponent>
    );
  };

  const tipPopover = () => {
    if (skipPayments) {
      return null;
    }
    return (
      <PopoverComponent
        referenceElement={tipReferenceElement}
        placement="right"
      >
        {popoverText("Customer Tip (2.9%)", providerTipFeeInt)}
      </PopoverComponent>
    );
  };

  const serviceFeeLabel = isCustomer ? (
    <span>Service Fee</span>
  ) : (
    <span ref={setServiceReferenceElement}>
      Merchant Services Fee <Icon name="info-16" color="#8F978C" />
    </span>
  );

  const tipFeeLabel = (
    <span ref={setTipReferenceElement} placement="right">
      Merchant Services Fee <Icon name="info-16" color="#8F978C" />
    </span>
  );
  const servicemap = () => {
    if (services.length < 1) {
      return <Service>No Service Selected</Service>;
    }
    return services.map((service) => {
      if (isCancelled) {
        service.attributes.price = null;
        service.attributes.duration = null;
      }

      if (service.attributes?.priceInt) {
        service.attributes.price = service.attributes.priceInt/100;
      }
      return <ServiceRow key={`${service.id}-service-row`} service={service} />;
    });
  };

  return (
    <>
      <HeaderContainer>
        <ServiceTableTitle>Services</ServiceTableTitle>
        {editHandler && !checkingOut && (
          <EditLink type="button" onClick={editHandler}>
            Edit
          </EditLink>
        )}
      </HeaderContainer>
      {servicemap()}
      {!!discount && !isCancelled && (
        <ServiceRow
          service={{
            attributes: { price: discount * -1, name: "Discount" },
          }}
        />
      )}
      {isCancelled && (
        <>
          <ServiceRow
            showBorder={false}
            service={{
              attributes: { price: cancellationFee, name: "Cancellation Fee" },
            }}
          />
          <CancellationFeeMessage cancellationFee={cancellationFee}>
            (Cancel or change {`${cancellationFee ? "after" : "before"}`} the
            cut-off period)
          </CancellationFeeMessage>
        </>
      )}
      {!!subtotal && !isCancelled && (
        <ServiceRow
          showBorder={false}
          service={{
            attributes: {
              price: subtotal,
              name: "Service total",
            },
          }}
        />
      )}
      {!!promoDiscount && !isCancelled && (
        <ServiceRow
          showBorder={false}
          service={{
            attributes: {
              price: promoDiscount * -1,
              name: "Promotional Discount",
            },
          }}
        />
      )}
      {!!tax && (
        <ServiceRow
          showBorder={false}
          service={{
            attributes: {
              price: tax,
              name: "Tax",
            },
          }}
        />
      )}
      {!!serviceFee && (
        <ServiceRow
          showBorder={false}
          service={{
            attributes: {
              price: isCustomer ? serviceFee : serviceFee * -1,
              name: serviceFeeLabel,
            },
          }}
        />
      )}
      {servicePopover()}
      {!!total && (
        <ServiceTotalWrapper showBorder>
          <ServicesTotal totalDuration={totalDuration()} totalPrice={total} />
        </ServiceTotalWrapper>
      )}
      {!!tipInt && (
        <HeaderContainer>
          <ServiceTableTitle>Tip</ServiceTableTitle>
          {editHandler && (
            <EditLink type="button" onClick={editHandler}>
              Edit
            </EditLink>
          )}
        </HeaderContainer>
      )}
      {!!tipInt && (
        <ServiceRow
          showBorder={false}
          service={{ attributes: { price: tipInt, name: "Tip Total" } }}
        />
      )}
      {!!providerTipFeeInt && (
        <ServiceRow
          showBorder
          showIcon
          service={{
            attributes: {
              price: providerTipFeeInt * -1,
              name: tipFeeLabel,
            },
          }}
        />
      )}
      {tipPopover()}
      {!!providerTipInt && (
        <ServiceTotalWrapper>
          <ServicesTotal title="Tip Total" totalPrice={providerTipInt} />
        </ServiceTotalWrapper>
      )}
      {/*  !!computedStatus &&
        ((hasTotal && (
          <>
            <ServiceTableTitle>
              <ServicesTotal
                title={`Total ${
                  (computedStatus === "completed" && "Paid") || "Unpaid"
                }`}
                totalPrice={totalWithTip}
              />
            </ServiceTableTitle>
            <CreditCardWrapper>
              <CardBrand brand={customerUserPaymentMethod?.brand} />
              <CardLastFourWrapper>
                **** {customerUserPaymentMethod?.last_four}
              </CardLastFourWrapper>
            </CreditCardWrapper>
          </>
        )) ||
          (!hasTotal && (
            <ServiceTotalWrapper>
              <ServicesTotal title="Total" totalPrice={null} />
            </ServiceTotalWrapper>
          ))) */}
    </>
  );
}

ServicesTable.propTypes = {
  cancellationFee: PropTypes.number,
  discount: PropTypes.number,
  editHandler: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
    PropTypes.string,
  ]),
  isCustomer: PropTypes.bool,
  promoDiscount: PropTypes.number,
  providerTipFeeInt: PropTypes.number,
  providerTipInt: PropTypes.number,
  serviceFee: PropTypes.number,
  services: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  skipPayments: PropTypes.bool,
  subtotal: PropTypes.number,
  tax: PropTypes.number,
  tipInt: PropTypes.number,
  total: PropTypes.number,
  // totalWithTip: PropTypes.number,
  checkingOut: PropTypes.bool,
  customerUserPaymentMethod: PropTypes.shape({
    brand: PropTypes.string,
    last_four: PropTypes.string,
  }),
  computedStatus: PropTypes.string,
};

ServicesTable.defaultProps = {
  cancellationFee: null,
  discount: null,
  editHandler: null,
  isCustomer: false,
  promoDiscount: undefined,
  providerTipFeeInt: undefined,
  providerTipInt: undefined,
  serviceFee: null,
  services: [],
  skipPayments: false,
  subtotal: undefined,
  tax: undefined,
  tipInt: null,
  total: undefined,
  // totalWithTip: undefined,
  checkingOut: false,
  customerUserPaymentMethod: undefined,
  computedStatus: undefined,
};

export default ServicesTable;
